<template>
  <DashboardWidget
    title="Kanban Boards"
    icon="fa fa-columns"
    :view-all-link="{ name: 'Kanban' }"
    :loading="loading"
    :is-empty="!hasBoards"
    empty-icon="fa fa-columns"
    empty-title="No boards yet"
    empty-text="Create a Kanban board to track projects"
  >
    <div class="kanban-summary">
      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-item">
          <span class="stat-value">{{ totalBoards }}</span>
          <span class="stat-label">Boards</span>
        </div>
        <div class="stat-item">
          <span class="stat-value cards">{{ totalCards }}</span>
          <span class="stat-label">Cards</span>
        </div>
        <div class="stat-item">
          <span class="stat-value columns">{{ totalColumns }}</span>
          <span class="stat-label">Columns</span>
        </div>
      </div>

      <!-- Boards List -->
      <div v-if="recentBoards.length > 0" class="boards-list">
        <div class="list-header">Your Boards</div>
        <div
          v-for="board in recentBoards"
          :key="board.id"
          class="list-item board-item"
          @click="goToBoard(board)"
        >
          <div class="item-avatar board-avatar" :style="getBoardStyle(board)">
            <i class="fa fa-th"></i>
          </div>
          <div class="item-content">
            <div class="item-header">
              <span class="item-title">{{ board.name }}</span>
              <span class="item-badge">{{ getCardCount(board) }} cards</span>
            </div>
            <div class="column-preview">
              <span
                v-for="(column, idx) in getColumnPreview(board)"
                :key="idx"
                class="column-chip"
              >
                {{ column.name }}: {{ getColumnTaskCount(column) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardWidget>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import DashboardWidget from './DashboardWidget.vue';
import mixin from '../../../mixin.js';
import { boardSortKey } from '@/utils/kanbanBoards';

export default {
  name: 'KanbanWidget',

  components: {
    DashboardWidget
  },

  mixins: [mixin],

  props: {
    maxItems: {
      type: Number,
      default: 3
    }
  },

  data() {
    return {
      isLoadingKanban: true
    };
  },

  computed: {
    ...mapState('kanban', ['boards']),

    loading() {
      return this.isLoadingKanban;
    },

    hasBoards() {
      return this.boards && this.boards.length > 0;
    },

    totalBoards() {
      return this.boards ? this.boards.length : 0;
    },

    totalCards() {
      if (!this.boards) return 0;
      return this.boards.reduce((total, board) => {
        return total + this.getCardCount(board);
      }, 0);
    },

    totalColumns() {
      if (!this.boards) return 0;
      return this.boards.reduce((total, board) => {
        return total + (board.columns ? board.columns.length : 0);
      }, 0);
    },

    recentBoards() {
      if (!this.boards) return [];
      // Most recently active first, top N. Shared sort key with the Kanban
      // dashboard (updatedAt -> lastModified -> createdAt fallback) so boards
      // without card activity still rank by creation instead of vanishing.
      return [...this.boards]
        .sort((a, b) => boardSortKey(b) - boardSortKey(a))
        .slice(0, this.maxItems);
    }
  },

  async mounted() {
    await this.loadBoards();
  },

  methods: {
    ...mapActions('kanban', ['fetchBoards']),

    async loadBoards() {
      this.isLoadingKanban = true;
      try {
        await this.fetchBoards({ siteID: this.siteID });
      } catch (error) {
        console.error('Error loading kanban boards:', error);
      } finally {
        this.isLoadingKanban = false;
      }
    },

    goToBoard(board) {
      this.$router.push({ name: 'KanbanBoard', params: { id: board.id } });
    },

    getCardCount(board) {
      if (!board.columns) return 0;
      return board.columns.reduce((count, column) => {
        return count + (column.tasks ? column.tasks.length : 0);
      }, 0);
    },

    getColumnPreview(board) {
      if (!board.columns) return [];
      return board.columns.slice(0, 3);
    },

    getColumnTaskCount(column) {
      return column.tasks ? column.tasks.length : 0;
    },

    getBoardStyle(board) {
      // Generate consistent color based on board name
      const colors = [
        '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
        '#10b981', '#06b6d4', '#6366f1', '#84cc16'
      ];
      const index = board.name ? board.name.charCodeAt(0) % colors.length : 0;
      return {
        backgroundColor: `${colors[index]}15`,
        color: colors[index]
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.kanban-summary {
  .stats-row {
    display: flex;
    justify-content: space-evenly;
    padding: var(--widget-stat-padding, 12px 20px);
    border-bottom: 1px solid #e8ecf1;
    margin-bottom: 0;
    min-height: 60px;
  }

  .stat-item {
    display: block !important;
    text-align: center;
    flex: 1;
    min-width: 0;
    padding: 0 8px !important;
    border-left: none !important;
  }

  .stat-value {
    display: block;
    font-size: var(--widget-stat-size, 1.5rem);
    font-weight: 700;
    color: #334155;
    line-height: 1.2;

    &.cards {
      color: #3b82f6;
    }

    &.columns {
      color: #8b5cf6;
    }
  }

  .stat-label {
    font-size: 0.6875rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
  }

  .boards-list {
    .list-header {
      font-size: 0.75rem;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: var(--widget-list-padding, 12px 20px 8px 20px);
    }
  }

  .board-item {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #f8fafc;
    }
  }

  .board-avatar {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 1rem;
    }
  }

  .item-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.6875rem;
    font-weight: 500;
    background-color: rgba(100, 116, 139, 0.1);
    color: #64748b;
  }

  .column-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;

    .column-chip {
      font-size: 0.6875rem;
      padding: 2px 6px;
      background-color: #f1f5f9;
      border-radius: 4px;
      color: #64748b;
    }
  }
}
</style>
